/**
 * **ONE COMPONENT FOR EVERY PAINTED UI ICON.** Care actions, pet stats, combat conditions and
 * elements all draw through this, exactly as the two currencies draw through `CurrencyIcon`.
 *
 * Deliberately not four components. `CareIcon`, `StatIcon`, `StatusIcon` and `ElementIcon` would
 * have been the same twelve lines four times, and four copies is how four families end up four
 * different sizes. The FAMILY lives in the map (`shared/domain/uiIconArt.ts`); this only draws.
 *
 * **IT FALLS BACK TO THE GLYPH, AND THAT IS THE POINT.** Every one of these icons has an emoji
 * that meant the same thing before it was painted, and the emoji is not retired — it is still
 * what a battle-log line, a Telegram message and a screen reader get. So if the artwork is
 * missing for any reason (an asset not yet landed, a bad deploy, a cache miss) the screen shows
 * exactly what it showed before the art existed rather than a broken-image box. A UI icon set is
 * not worth a chance of a broken arena.
 *
 * **Decorative by default.** Every one of these sits beside its own label — `❤️ Health`, a badge
 * with the word *Stunned* on it, a button with a tooltip — so the picture is the second telling
 * and `alt=""` is correct. Pass `label` only where the icon stands ALONE and nothing else names
 * it; then it becomes the accessible name instead of being hidden.
 */
import { useState } from 'react';
import './GameIcon.css';

interface GameIconProps {
  /** A `src` from one of the maps in `shared/domain/uiIconArt.ts`. Never a hand-written path. */
  src: string;
  /**
   * The glyph this icon replaced. Drawn instead of the image if the image cannot load, so a
   * missing asset degrades to the old screen rather than to a broken tile.
   */
  fallback?: string;
  /**
   * `inline` sits beside text and scales with it; `badge` is the combat HUD's status mark;
   * `action` is a care button's face; `hero` is artwork the screen is about.
   */
  size?: 'inline' | 'badge' | 'action' | 'hero';
  /**
   * The accessible name, for the rare icon with no text beside it. Omit — the default — and the
   * icon is hidden from screen readers, which is right wherever a label is already there.
   */
  label?: string;
  className?: string;
}

export function GameIcon({ src, fallback, size = 'inline', label, className }: GameIconProps) {
  const [broken, setBroken] = useState(false);
  const classes = `game-icon game-icon--${size}${className ? ` ${className}` : ''}`;

  if (broken && fallback) {
    return (
      <span className={`${classes} game-icon--glyph`} {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}>
        {fallback}
      </span>
    );
  }

  return (
    <img
      className={classes}
      src={src}
      alt={label ?? ''}
      {...(label ? {} : { 'aria-hidden': true })}
      onError={() => setBroken(true)}
      loading="lazy"
      decoding="async"
    />
  );
}
